import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Collapse,
  IconButton,
  Stack,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BedIcon from "@mui/icons-material/Bed";
import WarningIcon from "@mui/icons-material/Warning";
import LuggageIcon from "@mui/icons-material/Luggage";
import FiberNewIcon from "@mui/icons-material/FiberNew";

function buildLabel(p) {
  const room = p.lastNightRoomName || p.tonightRoomName || "No Room";
  return `${p.personName} (${room})`;
}

function isArrivingToday(p, selectedDate) {
  return p.arrivalDate === selectedDate;
}

function isDepartingToday(p) {
  return p.departingToday === 1 || p.departingToday === true;
}

function hasRoomForTonight(p) {
  return p.hasRoomForTonight === 1 || p.hasRoomForTonight === true;
}

function hadRoomLastNight(p) {
  return p.hadRoomLastNight === 1 || p.hadRoomLastNight === true;
}

function hasAnyRoom(p) {
  return Boolean(p.lastNightRoomName || p.tonightRoomName);
}

function hasLostRoom(p) {
  return hadRoomLastNight(p) && !hasRoomForTonight(p);
}

function isUnassignedHouse(house) {
  return String(house || "").trim().toLowerCase() === "unassigned";
}

function getHouseCardStyles(house, colorIndex) {
  if (isUnassignedHouse(house)) {
    return {
      backgroundColor: "#fdeaea",
      border: "1px solid #e6b3b3",
    };
  }

  const palette = [
    { backgroundColor: "#eaf4ea", border: "1px solid #b9d7b9" },
    { backgroundColor: "#eaf2fd", border: "1px solid #b8cdee" },
    { backgroundColor: "#fff8e6", border: "1px solid #ead79c" },
    { backgroundColor: "#f3ecfb", border: "1px solid #ccb7e8" },
    { backgroundColor: "#eaf7f6", border: "1px solid #b7dcd8" },
  ];

  return palette[colorIndex % palette.length];
}

export default function HouseCard({
  house,
  residents = [],
  selectedDate,
  colorIndex = 0,
  tv = false,
  tvColumns = 3,
}) {
  const [openState, setOpen] = useState(false);
  const open = tv || openState;
  const isUnassigned = isUnassignedHouse(house);

  return (
    <Card
      sx={{
        width: tv ? tvColumns * 250 : 300,
        ...getHouseCardStyles(house, colorIndex),
      }}
    >
      <CardContent>
        <Stack direction="row" justifyContent="space-between">
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: isUnassigned ? "error.main" : "inherit",
            }}
          >
            {house}
          </Typography>
          {tv ? null : (
            <IconButton onClick={() => setOpen((prev) => !prev)}>
              <ExpandMoreIcon />
            </IconButton>
          )}
        </Stack>

        <Typography variant="body2" sx={{ mt: 1 }}>
          {residents.length} residents
        </Typography>

        <Collapse in={open}>
          <Box sx={{ mt: 2, columnCount: tv ? tvColumns : 1, columnGap: "24px" }}>
            {residents.map((p) => (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                key={`${p.personType}-${p.personId}`}
                sx={{ breakInside: "avoid", mb: 1 }}
              >
                {isArrivingToday(p, selectedDate) && (
                  <FiberNewIcon color="primary" fontSize="small" />
                )}

                {isDepartingToday(p) && (
                  <LuggageIcon color="action" fontSize="small" />
                )}

                {hasLostRoom(p) && (
                  <WarningIcon color="warning" fontSize="small" />
                )}

                {hasAnyRoom(p) && (
                  <BedIcon fontSize="small" />
                )}

                <Chip
                  label={
                    <span
                      style={{
                        fontWeight: p.personType === "ADULT" ? 700 : 400,
                      }}
                    >
                      {buildLabel(p)}
                    </span>
                  }
                  color={hasRoomForTonight(p) ? "default" : "warning"}
                  size="small"
                />
              </Stack>
            ))}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}